import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Star, Send, Eye, Loader2, CheckCircle, Clock, Save, Trash2, RotateCcw } from "lucide-react";

const REVIEW_URL = "https://maps.app.goo.gl/TjmNecVF38dW6Z6V9";

const DEFAULT_BODY =
  "Dear {requester_name},\n\nWe hope you enjoyed your recent accessible bike session with our volunteer {volunteer_name}!\n\nYour experience means a great deal to us and to others who might benefit from this service. If you have a moment, we'd be so grateful if you could leave us a quick Google Review — it really helps us reach more people in the community.\n\nThank you so much for your time and support. It was a pleasure having you out on the bike!\n\nWarm regards,\nDodder Valley Cycling Club";

export default function AdminTestimonials() {
  const [bookings, setBookings]         = useState([]);
  const [loading, setLoading]           = useState(true);

  // Template state (bottom half editor)
  const [subject, setSubject]           = useState("");
  const [body, setBody]                 = useState("");
  const [templateLoaded, setTemplateLoaded] = useState(false);

  // Preview modal
  const [previewHtml, setPreviewHtml]   = useState(null);
  const [previewing, setPreviewing]     = useState(false);

  // Test email
  const [testEmail, setTestEmail]       = useState("");
  const [testModal, setTestModal]       = useState(false);
  const [testing, setTesting]           = useState(false);

  // Per-booking edit & send modal
  const [editModal, setEditModal]       = useState(null); // { booking, subject, body }
  const [sending, setSending]           = useState(null); // booking id being sent
  const [editSending, setEditSending]   = useState(false);
  const [saving, setSaving]             = useState(false);
  const [hideSent, setHideSent]         = useState(true);
  const [showHidden, setShowHidden]     = useState(false);
  const [selectedIds, setSelectedIds]   = useState(new Set());
  const [hiding, setHiding]             = useState(null); // booking id being hidden
  const [bulkHiding, setBulkHiding]     = useState(false);
  const [confirmBulkHide, setConfirmBulkHide] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const [tRes, sRes] = await Promise.all([api.getTestimonials(showHidden), api.getSettings()]);
      setBookings(tRes.data.bookings || []);
      const tmpl = sRes.data?.email_templates?.review_request || {};
      const savedSubject = tmpl.subject || "How did you find your accessible bike session? 🌟";
      const savedBody = tmpl.body || DEFAULT_BODY;
      setSubject(savedSubject);
      setBody(savedBody);
      setTemplateLoaded(true);
    } catch {
      toast.error("Failed to load testimonials");
    } finally {
      setLoading(false);
    }
  }, [showHidden]);

  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => { setSelectedIds(new Set()); }, [showHidden, hideSent]);

  const handlePreview = async (customBody) => {
    setPreviewing(true);
    try {
      const res = await api.previewEmail("review_request", customBody || body, null, null);
      setPreviewHtml(res.data.html);
    } catch {
      toast.error("Failed to generate preview");
    } finally {
      setPreviewing(false);
    }
  };

  const handleSendTest = async () => {
    setTesting(true);
    try {
      await api.sendTestEmail("review_request", testEmail);
      toast.success(`Test email sent to ${testEmail}`);
      setTestModal(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send test email");
    } finally {
      setTesting(false);
    }
  };

  const handleSaveTemplate = async () => {
    setSaving(true);
    try {
      const sRes = await api.getSettings();
      const existing = sRes.data?.email_templates || {};
      await api.updateSettings({
        email_templates: { ...existing, review_request: { subject, body } }
      });
      toast.success("Template saved successfully");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save template");
    } finally {
      setSaving(false);
    }
  };

  const handleSendDirect = async (booking) => {
    setSending(booking.id);
    try {
      await api.sendReviewRequest(booking.id, subject, body);
      toast.success(`Review request sent to ${booking.requester_name}`);
      setBookings((prev) => prev.map((b) => b.id === booking.id ? { ...b, review_sent: true } : b));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send review request");
    } finally {
      setSending(null);
    }
  };

  const openEditModal = (booking) => {
    setEditModal({ booking, subject, body });
  };

  const handleEditSend = async () => {
    setEditSending(true);
    try {
      await api.sendReviewRequest(editModal.booking.id, editModal.subject, editModal.body);
      toast.success(`Review request sent to ${editModal.booking.requester_name}`);
      setBookings((prev) => prev.map((b) => b.id === editModal.booking.id ? { ...b, review_sent: true } : b));
      setEditModal(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send review request");
    } finally {
      setEditSending(false);
    }
  };

  const handleHide = async (booking) => {
    setHiding(booking.id);
    try {
      await api.hideTestimonial(booking.id);
      toast.success(`${booking.requester_name} hidden from list`);
      if (showHidden) {
        setBookings((prev) => prev.map((b) => b.id === booking.id ? { ...b, review_excluded: true } : b));
      } else {
        setBookings((prev) => prev.filter((b) => b.id !== booking.id));
      }
      setSelectedIds((prev) => { const next = new Set(prev); next.delete(booking.id); return next; });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to hide booking");
    } finally {
      setHiding(null);
    }
  };

  const handleRestore = async (booking) => {
    setHiding(booking.id);
    try {
      await api.restoreTestimonial(booking.id);
      toast.success(`${booking.requester_name} restored to list`);
      setBookings((prev) => prev.map((b) => b.id === booking.id ? { ...b, review_excluded: false } : b));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to restore booking");
    } finally {
      setHiding(null);
    }
  };

  const handleBulkHide = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    setBulkHiding(true);
    try {
      const res = await api.bulkHideTestimonials(ids);
      toast.success(`${res.data.modified} booking${res.data.modified === 1 ? "" : "s"} hidden from list`);
      if (showHidden) {
        setBookings((prev) => prev.map((b) => ids.includes(b.id) ? { ...b, review_excluded: true } : b));
      } else {
        setBookings((prev) => prev.filter((b) => !ids.includes(b.id)));
      }
      setSelectedIds(new Set());
      setConfirmBulkHide(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to hide bookings");
    } finally {
      setBulkHiding(false);
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = (visible) => {
    setSelectedIds((prev) => {
      const visibleIds = visible.map((b) => b.id);
      const allSelected = visibleIds.every((id) => prev.has(id));
      if (allSelected) return new Set();
      return new Set(visibleIds);
    });
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-green-700" /></div>;
  }

  const visibleBookings = hideSent ? bookings.filter((b) => !b.review_sent) : bookings;
  const allVisibleSelected = visibleBookings.length > 0 && visibleBookings.every((b) => selectedIds.has(b.id));
  const someVisibleSelected = visibleBookings.some((b) => selectedIds.has(b.id));

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-800" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Testimonials
          </h1>
          <p className="text-slate-400 text-sm mt-1">
            Confirmed bookings older than 1 week — send a Google Review request to the requester. Rides auto-roll off the list 90 days after "Send" and after 12 months regardless.
          </p>
        </div>
        <a href={REVIEW_URL} target="_blank" rel="noreferrer"
          className="text-xs text-green-700 hover:text-green-900 font-medium flex items-center gap-1 transition-colors">
          <Star className="w-3.5 h-3.5" /> View our Google listing
        </a>
      </div>

      {/* ── TOP: Bookings table ── */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/50 flex items-center justify-between flex-wrap gap-3">
          <h2 className="font-semibold text-slate-700 text-sm" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Eligible Bookings ({visibleBookings.length}{hideSent && bookings.length !== visibleBookings.length ? ` of ${bookings.length}` : ""})
          </h2>
          <div className="flex items-center gap-3 flex-wrap">
            {selectedIds.size > 0 && (
              <Button
                size="sm"
                onClick={() => setConfirmBulkHide(true)}
                data-testid="bulk-hide-btn"
                className="bg-red-600 hover:bg-red-700 text-white text-xs gap-1.5 h-8">
                <Trash2 className="w-3.5 h-3.5" />
                Hide {selectedIds.size} selected
              </Button>
            )}
            <button
              onClick={() => setShowHidden((v) => !v)}
              data-testid="toggle-show-hidden-btn"
              className="text-xs text-slate-500 hover:text-slate-700 font-medium transition-colors">
              {showHidden ? "Hide removed bookings" : "Show removed bookings"}
            </button>
            <span className="text-slate-200">|</span>
            <button
              onClick={() => setHideSent((v) => !v)}
              data-testid="toggle-hide-sent-btn"
              className="text-xs text-slate-500 hover:text-slate-700 font-medium transition-colors">
              {hideSent ? "Show all (including sent)" : "Hide sent"}
            </button>
          </div>
        </div>
        {visibleBookings.length === 0 ? (
          <div className="px-6 py-12 text-center text-slate-400 text-sm">
            {hideSent && bookings.length > 0
              ? "All review requests have been sent. Click \"Show all\" to see the full list."
              : "No bookings are older than 1 week yet. Check back soon!"}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50/30 text-xs font-semibold text-slate-500 uppercase tracking-wide">
                  <th className="px-4 py-3 w-10">
                    <input
                      type="checkbox"
                      data-testid="select-all-testimonials"
                      checked={allVisibleSelected}
                      ref={(el) => { if (el) el.indeterminate = !allVisibleSelected && someVisibleSelected; }}
                      onChange={() => toggleSelectAll(visibleBookings)}
                      className="w-4 h-4 accent-red-600 cursor-pointer"
                      aria-label="Select all visible bookings"
                    />
                  </th>
                  <th className="px-5 py-3 text-left">Date</th>
                  <th className="px-5 py-3 text-left">Requester</th>
                  <th className="px-5 py-3 text-left">Email</th>
                  <th className="px-5 py-3 text-left">Volunteer</th>
                  <th className="px-5 py-3 text-center">Review Status</th>
                  <th className="px-5 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {visibleBookings.map((b) => (
                  <tr key={b.id}
                      className={`hover:bg-slate-50/40 transition-colors ${b.review_excluded ? "bg-slate-50/60 opacity-70" : ""} ${selectedIds.has(b.id) ? "bg-red-50/40" : ""}`}>
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        data-testid={`select-testimonial-${b.id}`}
                        checked={selectedIds.has(b.id)}
                        onChange={() => toggleSelect(b.id)}
                        className="w-4 h-4 accent-red-600 cursor-pointer"
                        aria-label={`Select ${b.requester_name}`}
                      />
                    </td>
                    <td className="px-5 py-3 text-slate-600 whitespace-nowrap">{b.booking_dt_formatted}</td>
                    <td className="px-5 py-3 font-medium text-slate-800">
                      {b.requester_name}
                      {b.review_excluded && (
                        <span className="ml-2 text-xs bg-slate-200 text-slate-600 px-2 py-0.5 rounded-full font-medium">Hidden</span>
                      )}
                    </td>
                    <td className="px-5 py-3 text-slate-500">{b.requester_email}</td>
                    <td className="px-5 py-3 text-slate-600">{b.claimed_by_name || <span className="text-slate-300">—</span>}</td>
                    <td className="px-5 py-3 text-center">
                      {b.review_sent ? (
                        <span data-testid={`review-sent-${b.id}`}
                          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-green-50 text-green-700 border border-green-200">
                          <CheckCircle className="w-3 h-3" /> Sent
                        </span>
                      ) : (
                        <span data-testid={`review-not-sent-${b.id}`}
                          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-500 border border-slate-200">
                          <Clock className="w-3 h-3" /> Not sent
                        </span>
                      )}
                    </td>
                    <td className="px-5 py-3 text-right">
                      <div className="flex gap-2 justify-end">
                        {b.review_excluded ? (
                          <Button size="sm" variant="outline" onClick={() => handleRestore(b)}
                            disabled={hiding === b.id}
                            data-testid={`restore-testimonial-${b.id}`}
                            className="border-slate-200 text-slate-600 hover:bg-slate-50 text-xs gap-1.5">
                            {hiding === b.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />}
                            Restore
                          </Button>
                        ) : (
                          <>
                            <Button size="sm" variant="outline" onClick={() => openEditModal(b)}
                              data-testid={`edit-send-review-${b.id}`}
                              className="border-slate-200 text-slate-600 hover:bg-slate-50 text-xs gap-1.5">
                              Edit & Send
                            </Button>
                            <Button size="sm" onClick={() => handleSendDirect(b)}
                              disabled={sending === b.id}
                              data-testid={`send-review-${b.id}`}
                              className="bg-[#15803d] hover:bg-green-800 text-white text-xs gap-1.5">
                              {sending === b.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                              Send
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => handleHide(b)}
                              disabled={hiding === b.id}
                              data-testid={`hide-testimonial-${b.id}`}
                              title="Hide from list (booking is preserved)"
                              className="border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700 text-xs gap-1.5">
                              {hiding === b.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                            </Button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── BOTTOM: Template editor ── */}
      {templateLoaded && (
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm">
          <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/50 flex items-center justify-between">
            <div>
              <h2 className="font-semibold text-slate-700 text-sm" style={{ fontFamily: "'Outfit', sans-serif" }}>
                Standard Email Template
              </h2>
              <p className="text-xs text-slate-400 mt-0.5">
                This is the default text used when clicking "Send" directly from the table above.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button onClick={() => handlePreview(body)} disabled={previewing}
                className="text-xs text-green-700 hover:text-green-900 font-medium flex items-center gap-1 transition-colors disabled:opacity-50">
                {previewing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Eye className="w-3.5 h-3.5" />}
                Preview
              </button>
              <span className="text-slate-200">|</span>
              <button onClick={() => setTestModal(true)}
                className="text-xs text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1 transition-colors">
                <Send className="w-3.5 h-3.5" /> Send Test
              </button>
              <span className="text-slate-200">|</span>
              <button onClick={handleSaveTemplate} disabled={saving}
                data-testid="save-review-template-btn"
                className="text-xs text-slate-700 hover:text-slate-900 font-medium flex items-center gap-1 transition-colors disabled:opacity-50">
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                {saving ? "Saving..." : "Save Template"}
              </button>
            </div>
          </div>
          <div className="p-6 space-y-4">
            {/* Available fields */}
            <div className="flex flex-wrap gap-2 items-center">
              <span className="text-xs text-slate-400 font-medium mr-1">Insert field:</span>
              {["{requester_name}", "{volunteer_name}"].map((f) => (
                <button key={f} onClick={() => setBody((b) => b + f)}
                  className="text-xs px-2 py-1 rounded-md bg-slate-100 hover:bg-green-50 hover:text-green-700 text-slate-600 font-mono transition-colors border border-slate-200">
                  {f}
                </button>
              ))}
            </div>
            <div>
              <Label className="text-slate-600 font-medium mb-1.5 block text-xs uppercase tracking-wide">Subject</Label>
              <Input value={subject} onChange={(e) => setSubject(e.target.value)}
                className="h-10 bg-slate-50 border-slate-200 text-sm" data-testid="review-subject-input" />
            </div>
            <div>
              <Label className="text-slate-600 font-medium mb-1.5 block text-xs uppercase tracking-wide">Email Body</Label>
              <textarea
                data-testid="review-body-textarea"
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={10}
                className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-green-600 focus:border-transparent resize-y font-mono leading-relaxed"
              />
            </div>
          </div>
        </div>
      )}

      {/* ── Preview Modal ── */}
      <Dialog open={!!previewHtml} onOpenChange={(o) => !o && setPreviewHtml(null)}>
        <DialogContent className="max-w-2xl w-full p-0 overflow-hidden">
          <DialogHeader className="px-6 py-4 border-b border-slate-100 bg-slate-50/50">
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
              <Eye className="w-4 h-4 text-green-700" /> Preview — Review Request Email
            </DialogTitle>
            <p className="text-xs text-slate-400 mt-0.5">Sample names shown. Actual email uses the requester's and volunteer's real names.</p>
          </DialogHeader>
          <div className="h-[560px] overflow-hidden">
            {previewHtml && (
              <iframe srcDoc={previewHtml} title="Email Preview" className="w-full h-full border-0" sandbox="allow-same-origin" data-testid="review-preview-iframe" />
            )}
          </div>
          <div className="px-6 py-3 border-t border-slate-100 bg-slate-50/50 flex justify-end">
            <Button variant="outline" onClick={() => setPreviewHtml(null)}>Close</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Send Test Modal ── */}
      <Dialog open={testModal} onOpenChange={(o) => { if (!o) setTestModal(false); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Outfit', sans-serif" }}>Send Test — Review Request</DialogTitle>
          </DialogHeader>
          <div className="py-2 space-y-4">
            <p className="text-sm text-slate-500">A test version of the review request will be sent using sample names.</p>
            <div>
              <Label className="font-semibold text-slate-700 mb-2 block">Test email address</Label>
              <Input type="email" placeholder="e.g. you@example.com" value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)} onKeyDown={(e) => e.key === "Enter" && handleSendTest()}
                className="h-11" autoFocus data-testid="review-test-email-input" />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setTestModal(false)} disabled={testing}>Cancel</Button>
            <Button onClick={handleSendTest} disabled={testing || !testEmail} data-testid="confirm-review-test-btn"
              className="bg-[#15803d] hover:bg-green-800 text-white gap-2">
              {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {testing ? "Sending..." : "Send Test"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Edit & Send Modal ── */}
      <Dialog open={!!editModal} onOpenChange={(o) => { if (!o) setEditModal(null); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Outfit', sans-serif" }}>
              Edit & Send — {editModal?.booking?.requester_name}
            </DialogTitle>
            <p className="text-xs text-slate-400 mt-0.5">Customise the email for this specific booking before sending.</p>
          </DialogHeader>
          {editModal && (
            <div className="space-y-4 py-2">
              <div className="flex flex-wrap gap-2 items-center">
                <span className="text-xs text-slate-400 font-medium mr-1">Insert:</span>
                {["{requester_name}", "{volunteer_name}"].map((f) => (
                  <button key={f} onClick={() => setEditModal((m) => ({ ...m, body: m.body + f }))}
                    className="text-xs px-2 py-1 rounded-md bg-slate-100 hover:bg-green-50 hover:text-green-700 text-slate-600 font-mono transition-colors border border-slate-200">
                    {f}
                  </button>
                ))}
              </div>
              <div>
                <Label className="text-slate-600 font-medium mb-1.5 block text-xs uppercase tracking-wide">Subject</Label>
                <Input value={editModal.subject} onChange={(e) => setEditModal({ ...editModal, subject: e.target.value })}
                  className="h-10 bg-slate-50 border-slate-200 text-sm" data-testid="edit-send-subject-input" />
              </div>
              <div>
                <Label className="text-slate-600 font-medium mb-1.5 block text-xs uppercase tracking-wide">Email Body</Label>
                <textarea
                  data-testid="edit-send-body-textarea"
                  value={editModal.body}
                  onChange={(e) => setEditModal({ ...editModal, body: e.target.value })}
                  rows={12}
                  className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-green-600 focus:border-transparent resize-y font-mono leading-relaxed"
                />
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
                <p className="text-xs text-amber-700">
                  <strong>Sending to:</strong> {editModal.booking.requester_name} ({editModal.booking.requester_email})
                </p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditModal(null)} disabled={editSending}>Cancel</Button>
            <Button onClick={() => handlePreview(editModal?.body)} disabled={previewing} variant="outline"
              className="gap-2 border-green-200 text-green-700 hover:bg-green-50">
              <Eye className="w-4 h-4" /> Preview
            </Button>
            <Button onClick={handleEditSend} disabled={editSending} data-testid="confirm-edit-send-btn"
              className="bg-[#15803d] hover:bg-green-800 text-white gap-2">
              {editSending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {editSending ? "Sending..." : "Send"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* ── Bulk Hide Confirmation ── */}
      <Dialog open={confirmBulkHide} onOpenChange={(o) => { if (!o) setConfirmBulkHide(false); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Outfit', sans-serif" }}>Hide {selectedIds.size} booking{selectedIds.size === 1 ? "" : "s"} from list?</DialogTitle>
          </DialogHeader>
          <div className="py-2 space-y-3">
            <p className="text-sm text-slate-600">
              The selected bookings will be removed from the Testimonials list so you won't see them here again.
            </p>
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-3">
              <p className="text-xs text-blue-800">
                <strong>Note:</strong> The bookings themselves are <strong>not deleted</strong> — they remain in Bookings, Archives, and Volunteer Hours. You can restore them anytime via "Show removed bookings".
              </p>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setConfirmBulkHide(false)} disabled={bulkHiding}>Cancel</Button>
            <Button onClick={handleBulkHide} disabled={bulkHiding} data-testid="confirm-bulk-hide-btn"
              className="bg-red-600 hover:bg-red-700 text-white gap-2">
              {bulkHiding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
              {bulkHiding ? "Hiding..." : `Hide ${selectedIds.size}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
